import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import NewsArticleCard from "../components/NewsArticleCard";
import NewsFilters from "../components/NewsFilters";
import { newsArticles, formatNewsDate, PILLAR_DOT } from "../data/newsArticles";

const News = () => {
  const [activeRubrics, setActiveRubrics] = useState([]);
  const [activePillars, setActivePillars] = useState([]);

  // Ordine cronologico inverso (newsArticles è già nell'ordine giusto)
  const sorted = useMemo(
    () => [...newsArticles].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)),
    []
  );

  const featured = sorted[0];

  const filtered = useMemo(() => {
    return sorted.filter((a) => {
      const rubricMatch =
        activeRubrics.length === 0 || activeRubrics.includes(a.rubric);
      const pillarMatch =
        activePillars.length === 0 ||
        activePillars.includes(a.pillarPrimary) ||
        a.pillarsSecondary?.some((p) => activePillars.includes(p));
      return rubricMatch && pillarMatch;
    });
  }, [sorted, activeRubrics, activePillars]);

  // Featured è in lista solo se sopravvive ai filtri; nella griglia mostriamo gli altri
  const featuredVisible = filtered.includes(featured);
  const grid = featuredVisible ? filtered.filter((a) => a.slug !== featured.slug) : filtered;

  const dot = PILLAR_DOT[featured.pillarPrimary] || "bg-lime";

  return (
    <>
      <Seo
        title="News e insight sponsorship sportiva italiana | OnSports"
        description="Manifesti, Sports Impact Observatory, Glossario, Inside the Deal. Analisi data-driven della sponsorship sportiva italiana dal team OnSports, aggiornata bisettimanale."
        path="/news"
      />

      {/* HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-20 md:pt-52 md:pb-24"
        data-testid="news-hero"
      >
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>News · Observatory · Editorial</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{ lineHeight: "0.95", letterSpacing: "-0.02em" }}
            >
              <span className="block">Insight, observatory,</span>
              <span className="block">
                manifesti dell'<span className="text-lime">IMPACT</span>.
              </span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.2}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Tutto quello che pubblichiamo apertamente — manifesti
              editoriali, deep-dive sui deal del mercato sportivo italiano,
              l'Osservatorio trimestrale, le voci del Glossario, i diari di
              campo. Aggiornato due volte a settimana.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.28}>
            <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Cadenza — Martedì &amp; Venerdì</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Aggiornato — Settimanale</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>{newsArticles.length} articoli in archivio</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-galactico py-14 md:py-20" data-testid="news-featured">
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to={`/news/${featured.slug}`}
              className="group block border border-mandrogni/30 hover:border-lime transition-colors duration-300"
              data-hover="true"
              data-testid="news-featured-card"
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                {/* Visual block */}
                <div
                  className="bg-dodgers text-galactico flex flex-col justify-between p-8 md:p-12"
                  style={{ minHeight: "320px" }}
                >
                  <div>
                    <span className="font-condensed uppercase tracking-widest text-lime text-[11px] md:text-[13px] block">
                      ● In primo piano · {featured.rubric}
                    </span>
                    <div
                      className="font-display text-lime mt-8 leading-[0.95]"
                      style={{ fontSize: "clamp(48px, 8vw, 96px)" }}
                    >
                      {String(featured.readingTime).padStart(2, "0")}
                    </div>
                    <span className="font-condensed uppercase tracking-widest text-galactico/80 text-[12px] mt-2 block">
                      Minuti di lettura
                    </span>
                  </div>
                  <div className="mt-10 space-y-2">
                    <span className="block h-[2px] w-16 bg-lime" />
                    <span className="block h-[2px] w-24 bg-lime opacity-70" />
                    <span className="block h-[2px] w-12 bg-lime opacity-40" />
                  </div>
                </div>
                {/* Content block */}
                <div className="bg-galactico p-8 md:p-12 flex flex-col">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${dot}`}
                      aria-hidden="true"
                    />
                    <span className="font-condensed uppercase tracking-widest text-lime text-[11px]">
                      {featured.rubric} · {featured.pillarPrimary}
                    </span>
                  </div>
                  <h2
                    className="mt-4 font-display text-dodgers leading-[1.05] group-hover:text-lime transition-colors"
                    style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}
                  >
                    {featured.title}
                  </h2>
                  <p
                    className="mt-4 text-mandrogni text-[15px] md:text-[16px] leading-[1.55]"
                    style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
                  >
                    {featured.subtitle}
                  </p>
                  <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-2 gap-y-1 font-condensed uppercase tracking-widest text-[11px] text-mandrogni">
                    <span>{featured.author}</span>
                    <span className="inline-block w-1 h-1 rounded-full bg-lime" aria-hidden="true" />
                    <span>{formatNewsDate(featured.publishedAt)}</span>
                  </div>
                </div>
              </div>
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* FILTRI */}
      <NewsFilters
        activeRubrics={activeRubrics}
        setActiveRubrics={setActiveRubrics}
        activePillars={activePillars}
        setActivePillars={setActivePillars}
        total={newsArticles.length}
        shown={filtered.length}
      />

      {/* GRIGLIA */}
      <section className="bg-galactico py-14 md:py-20" data-testid="news-grid">
        <div className="container-os">
          {grid.length === 0 ? (
            <div className="text-center py-16">
              <p
                className="text-dodgers text-[17px] leading-[1.6] max-w-[480px] mx-auto"
                style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
              >
                Nessun articolo corrisponde ai filtri selezionati. Prova a
                rimuovere alcuni filtri.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {grid.map((a) => (
                <NewsArticleCard key={a.slug} article={a} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section
        className="bg-dodgers text-galactico py-20 md:py-28 border-t border-galactico/10"
        data-testid="news-newsletter"
      >
        <div className="container-os">
          <div className="max-w-[760px]">
            <RevealOnScroll>
              <h2 className="display-h2">
                Ricevi i nostri insight prima di tutti gli altri. In{" "}
                <span className="text-lime">ANTICIPO</span>.
              </h2>
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <p className="mt-8 body-lg opacity-85">
                Newsletter mensile con il riassunto degli articoli del mese,
                l'estratto dell'Observatory trimestrale, gli early access ai
                prossimi report.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.18}>
              <div className="mt-10">
                <Link to="/newsletter" data-testid="newsletter-cta">
                  <Button variant="primary">Iscriviti →</Button>
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>
    </>
  );
};

export default News;
